/**
 * Optimizes Supabase Storage image URLs with transformations
 * Adds query parameters for automatic resizing, compression, and format conversion
 */

interface ImageTransformOptions {
  width?: number;
  height?: number;
  quality?: number; // 1-100, default is 80
  format?: 'webp' | 'avif' | 'origin'; // webp is best supported
  resize?: 'cover' | 'contain' | 'fill';
}

/**
 * Optimizes a Supabase image URL with automatic transformations
 * @param url - Original Supabase storage URL
 * @param options - Transformation options
 * @returns Optimized URL with transformation parameters
 */
export function optimizeImage(url: string, options: ImageTransformOptions = {}): string {
  // If it's a local path (starts with /), return as-is (served from public folder with full cache control)
  if (!url || url.startsWith('/')) {
    return url;
  }
  
  // Only transform Supabase storage URLs
  if (!url.includes('supabase.co/storage')) {
    return url;
  }

  // Default options for best performance
  const {
    width,
    height,
    quality = 80,
    format = 'webp',
    resize = 'cover'
  } = options;

  const params = new URLSearchParams();
  
  if (width) params.append('width', width.toString());
  if (height) params.append('height', height.toString());
  if (quality) params.append('quality', quality.toString());
  if (format && format !== 'origin') params.append('format', format);
  if (resize) params.append('resize', resize);

  // Append parameters to URL
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${params.toString()}`;
}

/**
 * Preset optimizations for common use cases
 */
export const ImagePresets = {
  // Product grid thumbnail (400x400)
  productThumbnail: (url: string) => optimizeImage(url, {
    width: 400,
    height: 400,
    quality: 80,
    format: 'webp',
    resize: 'cover'
  }),

  // Product detail page (800x800)
  productDetail: (url: string) => optimizeImage(url, {
    width: 800,
    height: 800,
    quality: 85,
    format: 'webp',
    resize: 'cover'
  }),

  // Category images (300x300)
  category: (url: string) => optimizeImage(url, {
    width: 300,
    height: 300,
    quality: 80,
    format: 'webp',
    resize: 'cover'
  }),

  // Small icons/badges (100x100)
  icon: (url: string) => optimizeImage(url, {
    width: 100,
    height: 100,
    quality: 75,
    format: 'webp',
    resize: 'cover'
  }),

  // Hero/banner images (1200x600)
  hero: (url: string) => optimizeImage(url, {
    width: 1200,
    height: 600,
    quality: 85,
    format: 'webp',
    resize: 'cover'
  }),
};
